import { AnalyticsContextValue, IResponseCartItem } from "@swiggy-private/checkout-core";
import type {
    IImpressionTurboCheckout,
    IClickTurboPaymentMethodChange,
    IClickTurboCheckout,
} from "@dineout-cart-client/interfaces/Cart";
import type { ILocalCartItem } from "@dineout-cart-client/interfaces/LocalCart";
import { ACTIONS, ACTION_TYPE, IMPRESSION_ACTIONS, CLICK_ACTIONS, CLICK_LABELS, SCREEN_NAME } from "../constants";

interface IImpressionTurboCheckoutStealDeal extends IImpressionTurboCheckout {
    finalBill?: number;
    savedAmount?: number;
}
interface IClickTurboPaymentMethodChangeStealDeal extends IClickTurboPaymentMethodChange {
    finalBill?: number;
    savedAmount?: number;
}
interface IClickTurboCheckoutStealDeal extends IClickTurboCheckout {
    finalBill?: number;
    savedAmount?: number;
}

export const impressionCancellationPolicyWidget = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_CC_POLICY_CART,
        context: JSON.stringify({ cartId: cartId ?? "" }),
    });
};

export const impressionSavingsBanner = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    billAmount?: number,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_DISCLAIMER_BANNER,
        context: JSON.stringify({
            billAmount,
        }),
    });
};

export const impressionBillViewChevron = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: string,
    context?: Record<string, unknown>,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_BILL_VIEW_CHEVRON,
        context: JSON.stringify({ cartId, ...context }),
    });
};

export const clickBillViewChevron = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId?: string,
    context?: Record<string, unknown>,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_BILL_VIEW_CHEVRON,
        context: JSON.stringify({ cartId, ...context }),
    });
};

export const impressionCollapsibleBillUpsell = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: string,
    upsellMessage?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_COLLAPSIBLE_BILL_UPSELL,
        context: JSON.stringify({ cartId, upsellMessage }),
    });
};

export const impressionDeliveryFeeLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_FEE_LINE_ITEM,
        label,
    });
};

export const impressionTaxesAndChargesLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
    cartId?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_TAXES_AND_CHARGES_POPUP,
        label,
        context: JSON.stringify({ cartId }),
    });
};

export const impressionDeliveryFeeUnCollapsedLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_FEE_UN_COLLAPSED_LINE_ITEM,
        label,
    });
};

export const impressionBillDetailsBanner = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: string,
    bannerType?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_BILL_DETAILS_BANNER,
        context: JSON.stringify({ cartId, bannerType }),
    });
};

export const clickBillDetailsBanner = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId?: string,
    bannerType?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_BILL_DETAILS_BANNER,
        context: JSON.stringify({ cartId, bannerType }),
    });
};

export const impressionBillDetailsBannerHalfcard = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: string,
    bannerType?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_BILL_DETAILS_BANNER_HALF_CARD,
        context: JSON.stringify({ cartId, bannerType }),
    });
};

export const clickBillDetailsBannerHalfcard = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId?: string,
    bannerType?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_BILL_DETAILS_BANNER_HALF_CARD,
        context: JSON.stringify({ cartId, bannerType }),
    });
};

export const impressionStealStoreCart = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartAnalyticsData?: {
        voucherList: IResponseCartItem[];
        originalBill: number;
        finalBill: number;
        savedAmount: number;
    } | null,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_STEAL_STORE_CART,
        context: JSON.stringify({ ...cartAnalyticsData }),
    });
};

export const impressionTurboCheckout = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    prop: IImpressionTurboCheckoutStealDeal,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_TURBO_CHECKOUT,
        label: ACTION_TYPE.PAY_BTN_VIEW,
        context: JSON.stringify(prop),
    });
};

export const clickTurboPaymentMethodChange = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    prop: IClickTurboPaymentMethodChangeStealDeal,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: ACTIONS.CLICK_TURBO_PAYMENT_CHANGE,
        label: "",
        context: JSON.stringify(prop),
    });
};

export const clickTurboCheckout = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    prop: IClickTurboCheckoutStealDeal,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: ACTIONS.CLICK_TURBO_CHECKOUT,
        label: ACTION_TYPE.PAY_BTN_CLICK,
        context: JSON.stringify(prop),
    });
};

export const impressionEligibleOutlets = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    item?: Record<string, unknown>,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_ITEM,
        context: JSON.stringify({ ...item }),
    });
};

export const clickEligibleOutlets = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    eligibleOutlets?: string[],
    cartId?: string,
    itemId?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_ITEM,
        context: JSON.stringify({
            itemId: itemId ?? "",
            eligibleOutlets: eligibleOutlets ?? [],
            cartId: cartId ?? "",
        }),
    });
};

export const clickStealStoreCartBack = (clickEvent: AnalyticsContextValue["clickEvent"], cartId?: string): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_BACK_BTN,
        label: CLICK_LABELS.BACK_CLICK,
        context: JSON.stringify({ cartId: cartId ?? "" }),
    });
};

export const clickStealStoreItem = (clickEvent: AnalyticsContextValue["clickEvent"], item?: ILocalCartItem): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_TICKET_ITEM_UPDATE,
        context: JSON.stringify({ itemId: item?.itemId, itemVariantId: item?.itemVariantId, quantity: item?.quantity }),
    });
};

export const clickStealStoreDeleteItem = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    item?: ILocalCartItem,
): void => {
    clickEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: CLICK_ACTIONS.CLICK_DELETE_TICKET_ITEM,
        context: JSON.stringify({ itemId: item?.itemId, itemVariantId: item?.itemVariantId, quantity: item?.quantity }),
    });
};

export const impressionTurboPayBtn = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    prop: IImpressionTurboCheckoutStealDeal,
): void => {
    impressionEvent({
        category: SCREEN_NAME.STEAL_STORE_CART,
        action: IMPRESSION_ACTIONS.IMPRESSION_TURBO_PAY_BTN,
        context: JSON.stringify(prop),
    });
};
